import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Bug,
  Building2,
  CircleArrowDown,
  CircleArrowUp,
  CircleDollarSign,
  Clock,
  FileText,
  Globe,
  Hash,
  Phone,
  Plug,
  ShieldCheck,
  Ship,
  Syringe,
  Utensils,
  BriefcaseMedical,
  Car,
  Banknote,
} from 'lucide-react';
import { countryInfoEn as copy } from '../../content/countryInfo.en';
import type { CountryDataDto, EmbassyDto, PortDto } from '../../services/countryInfoApi';
import {
  buildCommonConversions,
  fetchExchangeRate,
  type ExchangeRate,
} from '../../services/currencyExchangeService';
import { plugTypeImageUrl } from '../../utils/plugImages';
import { rideHailLogoUrl } from '../../utils/rideHailLogos';
import {
  electricalInfoForCountry,
  needsAdapterWarning,
  plugTypeDescription,
  tippingInfoForIso2,
  visaInfoFor,
  type VisaInfo,
} from '../../utils/countryInfoLookups';
import { showsWallOutletSwitchWarning } from '../../utils/electricalWallSwitch';
import CollapsibleSection from './CollapsibleSection';
import InfoRow from './InfoRow';
import styles from './CountryInfo.module.css';

function splitCompanies(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export const TimeZonePhoneSection: React.FC<{
  homeTz: string;
  destTz: string;
  phoneCode: string | null | undefined;
}> = ({ homeTz, destTz, phoneCode }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const fmt = (tz: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: tz || 'UTC',
      }).format(now);
    } catch {
      return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(now);
    }
  };

  return (
    <div className={styles.timePhoneCard}>
      <div className={styles.clockRow}>
        <div className={styles.clockBlock}>
          <div className={styles.clockLabel}>{copy.home}</div>
          <div className={styles.clockTime}>{fmt(homeTz)}</div>
        </div>
        <div className={styles.clockBlock}>
          <div className={styles.clockLabel}>{destTz || 'UTC'}</div>
          <div className={styles.clockTime}>{fmt(destTz || 'UTC')}</div>
        </div>
      </div>
      {phoneCode ? (
        <div className={styles.phoneRow}>
          <Phone size={22} className={styles.phoneIcon} aria-hidden />
          <span>
            {copy.countryCode}: +{String(phoneCode).replace(/^\+/, '')}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export const KnowBeforeYouGoSection: React.FC<{
  country: CountryDataDto;
  citizenIso2: string | null;
}> = ({ country, citizenIso2 }) => {
  const visa = visaInfoFor(citizenIso2, country.iso2);
  const electrical = electricalInfoForCountry(country.country);
  return (
    <CollapsibleSection
      title={copy.knowBeforeTitle}
      subtitle={copy.knowBeforeSubtitle}
      icon={<ShieldCheck size={22} className={styles.colorBlue} />}
      defaultExpanded
    >
      <VisaBlock
        visa={visa}
        entry={country.entryRequirementsSummary}
        hasNationality={Boolean(citizenIso2?.trim())}
      />
      <ElectricalBlock
        countryName={country.country}
        iso2={country.iso2}
        info={electrical}
      />
    </CollapsibleSection>
  );
};

const VisaBlock: React.FC<{
  visa: VisaInfo | null;
  entry?: string | null;
  hasNationality: boolean;
}> = ({ visa, entry, hasNationality }) => {
  const [open, setOpen] = useState(false);
  const missingCopy = hasNationality ? copy.visaMissing : copy.visaNeedNationality;
  return (
    <div className={styles.nestedCard}>
      <button type="button" className={styles.sectionHeader} onClick={() => setOpen((v) => !v)}>
        <span className={styles.sectionIcon}>
          <FileText size={20} className={styles.colorBlue} />
        </span>
        <span className={styles.sectionTitles}>
          <h3 className={styles.sectionTitle}>{copy.visaTitle}</h3>
          {visa ? (
            <p className={styles.sectionSubtitle}>
              <span className={`${styles.badge} ${visa.visaRequired ? styles.badgeWarn : styles.badgeOk}`}>
                {visa.visaRequired ? (
                  <AlertCircle size={12} aria-hidden />
                ) : (
                  <ShieldCheck size={12} aria-hidden />
                )}
                {visa.visaRequired ? copy.visaRequired : copy.visaNotRequired}
              </span>
              {visa.visaDuration ? ` · ${visa.visaDuration}` : ''}
            </p>
          ) : (
            <p className={styles.sectionSubtitle}>{missingCopy}</p>
          )}
        </span>
      </button>
      {open ? (
        <div className={styles.sectionBody}>
          {visa ? (
            <>
              <InfoRow
                icon={
                  visa.visaRequired ? (
                    <AlertCircle size={16} className={styles.colorRed} />
                  ) : (
                    <ShieldCheck size={16} className={styles.colorGreen} />
                  )
                }
                label={copy.visaStatus}
                value={visa.visaRequired ? copy.visaRequired : copy.visaNotRequired}
              />
              {visa.visaDuration ? (
                <InfoRow
                  icon={<Clock size={16} className={styles.colorBlue} />}
                  label={copy.visaMaxStay}
                  value={visa.visaDuration}
                />
              ) : null}
            </>
          ) : (
            <p className={styles.muted}>{missingCopy}</p>
          )}
          {entry?.trim() ? (
            <InfoRow
              icon={<AlertCircle size={16} className={styles.colorOrange} />}
              label={copy.entryRequirements}
              value={entry}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ElectricalBlock: React.FC<{
  countryName: string;
  iso2?: string | null;
  info: ReturnType<typeof electricalInfoForCountry>;
}> = ({ countryName, iso2, info }) => {
  const [open, setOpen] = useState(false);
  const warn = info ? needsAdapterWarning(info.plugTypes) : false;
  const wallSwitch = showsWallOutletSwitchWarning({
    iso2,
    country: countryName,
    adapterInfo: info,
  });
  return (
    <div className={styles.nestedCard}>
      <button type="button" className={styles.sectionHeader} onClick={() => setOpen((v) => !v)}>
        <span className={styles.sectionIcon}>
          <Plug size={20} className={styles.colorOrange} />
        </span>
        <span className={styles.sectionTitles}>
          <h3 className={styles.sectionTitle}>{copy.electricalTitle}</h3>
          <p className={styles.sectionSubtitle}>
            {info ? `${info.plugTypes.join(', ')} · ${info.voltage}` : copy.electricalMissing}
          </p>
        </span>
      </button>
      {open ? (
        <div className={styles.sectionBody}>
          {info ? (
            <>
              {warn ? (
                <div className={styles.warningBanner}>
                  <AlertTriangle size={16} aria-hidden />
                  <span>{copy.adapterWarning}</span>
                </div>
              ) : null}
              {wallSwitch ? (
                <div className={styles.warningBanner}>
                  <AlertTriangle size={16} aria-hidden />
                  <span>{copy.wallOutletSwitchWarning}</span>
                </div>
              ) : null}
              <div className={styles.plugGrid}>
                {info.plugTypes.map((t) => {
                  const src = plugTypeImageUrl(t);
                  return (
                    <div key={t} className={styles.plugItem}>
                      {src ? (
                        <img src={src} alt={`Type ${t}`} className={styles.plugImg} />
                      ) : (
                        <Plug size={40} className={styles.colorOrange} />
                      )}
                      <div className={styles.plugLabel}>
                        Type {t}
                        <br />
                        {plugTypeDescription(t)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <InfoRow
                icon={<Plug size={16} className={styles.colorOrange} />}
                label={copy.voltage}
                value={info.voltage}
              />
              <InfoRow
                icon={<Clock size={16} className={styles.colorBlue} />}
                label={copy.frequency}
                value={info.frequency}
              />
            </>
          ) : (
            <p className={styles.muted}>{copy.electricalMissing}</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export const HealthSafetySection: React.FC<{ country: CountryDataDto }> = ({ country }) => {
  const cards: Array<{
    key: string;
    title: string;
    body: string;
    icon: React.ReactNode;
    tone: 'severe' | 'moderate' | 'info';
  }> = [];
  if (country.vaccinesRequired?.trim()) {
    cards.push({
      key: 'req',
      title: copy.vaccinesRequired,
      body: country.vaccinesRequired,
      icon: <AlertTriangle size={16} className={styles.colorRed} />,
      tone: 'severe',
    });
  }
  if (country.vaccinesRecommended?.trim()) {
    cards.push({
      key: 'rec',
      title: copy.vaccinesRecommended,
      body: country.vaccinesRecommended,
      icon: <Syringe size={16} className={styles.colorOrange} />,
      tone: 'moderate',
    });
  }
  if (country.routineVaccinesRecommended?.trim()) {
    cards.push({
      key: 'routine',
      title: copy.vaccinesRoutine,
      body: country.routineVaccinesRecommended,
      icon: <ShieldCheck size={16} className={styles.colorBlue} />,
      tone: 'info',
    });
  }
  if (country.healthRisks?.trim()) {
    cards.push({
      key: 'risks',
      title: copy.healthRisks,
      body: country.healthRisks,
      icon: <AlertCircle size={16} className={styles.colorOrange} />,
      tone: 'moderate',
    });
  }
  if (country.malariaChemo?.trim()) {
    cards.push({
      key: 'malaria',
      title: copy.malaria,
      body: country.malariaChemo,
      icon: <Bug size={16} className={styles.colorRed} />,
      tone: 'severe',
    });
  }
  if (country.foodWaterSafety?.trim()) {
    cards.push({
      key: 'food',
      title: copy.foodWater,
      body: country.foodWaterSafety,
      icon: <Utensils size={16} className={styles.colorBlue} />,
      tone: 'info',
    });
  }
  if (country.insectBitePrevention?.trim()) {
    cards.push({
      key: 'insect',
      title: copy.insect,
      body: country.insectBitePrevention,
      icon: <Bug size={16} className={styles.colorGreen} />,
      tone: 'info',
    });
  }

  return (
    <CollapsibleSection
      title={copy.healthTitle}
      subtitle={cards.length ? copy.tapToExpand : copy.healthMissing}
      icon={<BriefcaseMedical size={22} className={styles.colorRed} />}
    >
      {cards.length === 0 ? (
        <p className={styles.muted}>{copy.healthMissing}</p>
      ) : (
        cards.map((c) => (
          <div
            key={c.key}
            className={`${styles.healthCard} ${
              c.tone === 'severe'
                ? styles.healthSevere
                : c.tone === 'moderate'
                  ? styles.healthModerate
                  : styles.healthInfo
            }`}
          >
            <InfoRow icon={c.icon} label={c.title} value={c.body} />
          </div>
        ))
      )}
    </CollapsibleSection>
  );
};

export const EmbassySection: React.FC<{
  embassies: EmbassyDto[];
  hasNationality: boolean;
}> = ({ embassies, hasNationality }) => {
  const embassy = embassies.find((e) => e.isActive !== false) ?? embassies[0] ?? null;
  return (
    <CollapsibleSection
      title={copy.embassyTitle}
      subtitle={embassy ? embassy.city : undefined}
      icon={<Building2 size={22} className={styles.colorBlue} />}
      defaultExpanded
    >
      {embassy ? (
        <>
          {embassy.mainPhoneInternational || embassy.mainPhoneLocal ? (
            <InfoRow
              icon={<Phone size={16} className={styles.colorGreen} />}
              label={copy.embassyMainPhone}
              value={embassy.mainPhoneInternational || embassy.mainPhoneLocal}
            />
          ) : null}
          {embassy.afterHoursPhone ? (
            <InfoRow
              icon={<Phone size={16} className={styles.colorRed} />}
              label={copy.embassyEmergency}
              value={embassy.afterHoursPhone}
            />
          ) : null}
          {embassy.address ? (
            <InfoRow
              icon={<Building2 size={16} className={styles.colorBlue} />}
              label={copy.embassyAddress}
              value={embassy.address}
            />
          ) : null}
          {embassy.embassyURL ? (
            <InfoRow
              icon={<Globe size={16} className={styles.colorPurple} />}
              label={copy.embassyWebsite}
              value={
                <a className={styles.link} href={embassy.embassyURL} target="_blank" rel="noreferrer">
                  {embassy.embassyURL}
                </a>
              }
            />
          ) : null}
        </>
      ) : (
        <p className={styles.muted}>
          {hasNationality ? copy.embassyMissing : copy.embassyNeedNationality}
        </p>
      )}
    </CollapsibleSection>
  );
};

export const CurrencyExchangeSection: React.FC<{
  homeCurrency: string | null;
  destCurrency: string | null;
  destCurrencyName?: string | null;
  refreshKey: number;
}> = ({ homeCurrency, destCurrency, destCurrencyName, refreshKey }) => {
  const [rate, setRate] = useState<ExchangeRate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!homeCurrency || !destCurrency) {
        setRate(null);
        return;
      }
      setLoading(true);
      setError(false);
      const result = await fetchExchangeRate(homeCurrency, destCurrency);
      if (cancelled) return;
      setRate(result);
      setError(!result);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [homeCurrency, destCurrency, refreshKey]);

  const conversions = useMemo(
    () => (rate ? buildCommonConversions(rate.rate) : []),
    [rate]
  );

  return (
    <CollapsibleSection
      title={copy.currencyTitle}
      subtitle={destCurrencyName || destCurrency || copy.currencyMissing}
      icon={<CircleDollarSign size={22} className={styles.colorGreen} />}
      defaultExpanded
    >
      {!homeCurrency || !destCurrency ? (
        <p className={styles.muted}>{copy.currencyMissing}</p>
      ) : loading ? (
        <p className={styles.muted}>{copy.currencyLoading}</p>
      ) : rate ? (
        <>
          <p className={styles.rateGreen}>
            {copy.currencyRateLabel}: 1 {rate.fromCurrency} → {rate.rate.toFixed(4)}{' '}
            {rate.toCurrency}
          </p>
          <div className={styles.infoRowLabel}>{copy.currencyQuickRef}</div>
          <div className={styles.fxGrid}>
            {conversions.map((c, i) => (
              <div key={i} className={styles.fxCell}>
                {Math.round(c.converted).toLocaleString()} {rate.toCurrency} ={' '}
                {Math.round(c.amount).toLocaleString()} {rate.fromCurrency}
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className={styles.muted}>{error ? copy.currencyUnable : copy.currencyMissing}</p>
      )}
    </CollapsibleSection>
  );
};

export const TippingSection: React.FC<{ iso2: string | null }> = ({ iso2 }) => {
  const info = tippingInfoForIso2(iso2);
  const meals = info?.meals || info?.restaurants || '';
  const shortTours = info?.shortTours || info?.tours || '';
  const categories: Array<{ label: string; value: string; icon: React.ReactNode }> = info
    ? [
        {
          label: copy.tippingRestaurants,
          value: meals,
          icon: <Utensils size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingBar,
          value: info.bar || '',
          icon: <Banknote size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingShortTours,
          value: shortTours,
          icon: <Car size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingAllDayTours,
          value: info.allDayTours || '',
          icon: <Car size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingTaxis,
          value: info.taxis || '',
          icon: <Car size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingPorter,
          value: info.hotelPorter || '',
          icon: <Building2 size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingValet,
          value: info.valet || '',
          icon: <Car size={16} className={styles.colorPurple} />,
        },
        {
          label: copy.tippingSpa,
          value: info.spa || '',
          icon: <Banknote size={16} className={styles.colorPurple} />,
        },
      ].filter((row) => row.value.trim())
    : [];

  return (
    <CollapsibleSection
      title={copy.tippingTitle}
      icon={<Banknote size={22} className={styles.colorPurple} />}
      collapsible={false}
    >
      {info ? (
        <>
          {typeof info.isAcceptable === 'boolean' ? (
            <p className={styles.sectionSubtitle}>
              <span
                className={`${styles.badge} ${
                  info.isAcceptable ? styles.badgeOk : styles.badgeWarn
                }`}
              >
                {info.isAcceptable ? copy.tippingAccepted : copy.tippingNotCustomary}
              </span>
            </p>
          ) : null}
          {info.summary?.trim() ? (
            <InfoRow
              icon={<Banknote size={16} className={styles.colorPurple} />}
              label={copy.tippingSummary}
              value={info.summary}
            />
          ) : null}
          {categories.map((row) => (
            <InfoRow key={row.label} icon={row.icon} label={row.label} value={row.value} />
          ))}
        </>
      ) : (
        <p className={styles.muted}>{copy.tippingMissing}</p>
      )}
    </CollapsibleSection>
  );
};

export const RideHailSection: React.FC<{ companiesRaw: string | null | undefined }> = ({
  companiesRaw,
}) => {
  const companies = splitCompanies(companiesRaw);
  return (
    <CollapsibleSection
      title={copy.rideHailTitle}
      subtitle={companies.length ? copy.rideHailAvailable : copy.rideHailMissing}
      icon={<Car size={22} className={styles.colorBlue} />}
      collapsible={false}
    >
      {companies.length === 0 ? (
        <p className={styles.muted}>{copy.rideHailMissing}</p>
      ) : (
        <div className={styles.rideGrid}>
          {companies.map((name) => {
            const logo = rideHailLogoUrl(name);
            return (
              <div key={name} className={styles.rideItem}>
                {logo ? (
                  <img src={logo} alt={name} className={styles.rideLogo} />
                ) : (
                  <div className={styles.rideFallback}>
                    <Car size={22} aria-hidden />
                  </div>
                )}
                <span className={styles.rideName}>{name}</span>
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleSection>
  );
};

export const PortInfoSection: React.FC<{
  port: PortDto | null;
  dockNumber?: string | null;
  arrivalHm?: string | null;
  departureHm?: string | null;
}> = ({ port, dockNumber, arrivalHm, departureHm }) => {
  return (
    <CollapsibleSection
      title={copy.portTitle}
      subtitle={port?.officialName}
      icon={<Ship size={22} className={styles.colorBlue} />}
    >
      {!port ? (
        <p className={styles.muted}>{copy.portMissing}</p>
      ) : (
        <>
          {port.phonePrimary ? (
            <InfoRow
              icon={<Phone size={16} className={styles.colorGreen} />}
              label={copy.portPhone}
              value={port.phonePrimary}
            />
          ) : null}
          {port.phoneAfterHours ? (
            <InfoRow
              icon={<Phone size={16} className={styles.colorRed} />}
              label={copy.portAfterHours}
              value={port.phoneAfterHours}
            />
          ) : null}
          {port.address ? (
            <InfoRow
              icon={<Building2 size={16} className={styles.colorBlue} />}
              label={copy.portAddress}
              value={port.address}
            />
          ) : null}
          {port.website ? (
            <InfoRow
              icon={<Globe size={16} className={styles.colorPurple} />}
              label={copy.portWebsite}
              value={
                <a className={styles.link} href={port.website} target="_blank" rel="noreferrer">
                  {port.website}
                </a>
              }
            />
          ) : null}
          {(port.latitude ?? port.latitudeGeocoded) != null &&
          (port.longitude ?? port.longitudeGeocoded) != null ? (
            <InfoRow
              icon={<Hash size={16} className={styles.colorBlue} />}
              label={copy.portCoords}
              value={`${port.latitude ?? port.latitudeGeocoded}, ${port.longitude ?? port.longitudeGeocoded}`}
            />
          ) : null}
          {dockNumber ? (
            <InfoRow
              icon={<Hash size={16} className={styles.colorBlue} />}
              label={copy.portDock}
              value={dockNumber}
            />
          ) : null}
          {arrivalHm ? (
            <InfoRow
              icon={<CircleArrowDown size={16} className={styles.colorGreen} />}
              label={copy.portArrival}
              value={arrivalHm}
            />
          ) : null}
          {departureHm ? (
            <InfoRow
              icon={<CircleArrowUp size={16} className={styles.colorOrange} />}
              label={copy.portDeparture}
              value={departureHm}
            />
          ) : null}
        </>
      )}
    </CollapsibleSection>
  );
};
